import { BodyText, Button, ButtonProps, Heading, Modal } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { formatDate } from '@jusbrasil-web/shared-utils-format'
import { useEffect, useMemo } from 'react'

import { useIdDocumentValidationContext } from '../context'
import { Steps } from '../enums'
import styles from './steps.module.scss'

export interface DocumentStaticStepProps {
  imageSrc: string
  title: string
  bodyText: React.ReactNode
  bodyText2?: React.ReactNode
  action?: Omit<ButtonProps, 'children'> & {
    nextStep?: Steps
    label: React.ReactNode
  }
  action2?: Omit<ButtonProps, 'children'> & {
    label: React.ReactNode
  }
  closeButton?: Omit<ButtonProps, 'children'> & {
    label: React.ReactNode
  }
  testId?: string
  onLoadEvent?: {
    name: string
    metadata?: Record<string, unknown>
    data?: Record<string, unknown>
  }
}

export function DocumentStaticStep(props: DocumentStaticStepProps) {
  const {
    action,
    action2,
    closeButton,
    imageSrc,
    title,
    bodyText,
    bodyText2,
    testId,
    onLoadEvent,
  } = props
  const {
    setCurrentStep,
    sharedData,
    eventData: eventContextData,
    viewer,
  } = useIdDocumentValidationContext()
  const pid = viewer?.pid.toString() || null

  const identityMetadata = useMemo(
    () =>
      sharedData?.cpf
        ? {
            cpf: sharedData.cpf,
            birthDate: formatDate(sharedData.birthDate?.getTime() || ''),
          }
        : {},
    [sharedData?.cpf, sharedData?.birthDate]
  )

  const eventData = useMemo(
    () => ({
      ...onLoadEvent?.data,
      ...eventContextData,
      metadata: {
        ...identityMetadata,
        ...onLoadEvent?.metadata,
        ...eventContextData?.metadata,
      },
    }),
    [onLoadEvent?.data, onLoadEvent?.metadata, eventContextData, identityMetadata]
  )

  useEffect(() => {
    if (onLoadEvent) sendMetrics(onLoadEvent.name, pid, eventData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadEvent?.name, pid])

  const { label: _actionLabel, nextStep: _actionNextStep, ...actionProps } = action || {}
  const { label: _action2Label, ...action2Props } = action2 || {}
  const { label: _closeButtonLabel, ...closeButtonProps } = closeButton || {}

  return (
    <>
      <Modal.Body className={styles.body} data-testid={testId}>
        <Image alt={title} src={imageSrc} height={140} width={140} className={styles.image} />
        <Heading size="md" className={styles.title}>
          {title}
        </Heading>
        <BodyText size="md" className={styles.text}>
          {bodyText}
        </BodyText>
        {bodyText2 && (
          <BodyText size="md" className={styles.text}>
            {bodyText2}
          </BodyText>
        )}
      </Modal.Body>
      {!!action && (
        <Modal.Footer className={closeButton ? styles.modalFooter : ''}>
          {!!closeButton && (
            <Button
              {...closeButtonProps}
              onClick={(event) => {
                if (closeButton.onClick) closeButton.onClick(event)
              }}
            >
              {closeButton.label}
            </Button>
          )}
          {!!action2 && (
            <Button
              kind="primary"
              {...action2Props}
              onClick={(event) => {
                if (action2.onClick) action2.onClick(event)
              }}
            >
              {action2.label}
            </Button>
          )}

          <Button
            kind={action2 ? 'tertiary' : 'primary'}
            {...actionProps}
            onClick={(event) => {
              if (action.onClick) action.onClick(event)
              if (action.nextStep) setCurrentStep(action.nextStep)
            }}
          >
            {action.label}
          </Button>
        </Modal.Footer>
      )}
    </>
  )
}
